import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, IconButton, Tooltip, Stack,
  CircularProgress, Autocomplete, FormControlLabel, Checkbox, MenuItem,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import { TableKit } from '@NP-Webber/crm-kit';
import AudioPlayer from '../../components/AudioPlayer.jsx';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import { OCCUPATION_OPTIONS } from '../../utils/memberStudy.js';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';

const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
const SYSTEM_ID = String(process.env.REACT_APP_SYSTEM_ID || '').replace(/^["']|["']$/g, '').trim();
const IS_MISHTAMERET = SYSTEM_ID === 'waiting_client';

const STATUS_LABEL = {
  pending:  { label: 'ממתין', color: 'warning' },
  approved: { label: 'אושר',  color: 'success' },
  rejected: { label: 'נדחה',  color: 'error' },
};

const COLUMNS = [
  {
    key: 'createdAt', title: 'תאריך', sortable: true, minWidth: '130px',
    render: (v) => v ? formatHebrewDateTime(v) : '—',
  },
  { key: 'phone', title: 'טלפון מתקשר', sortable: true, minWidth: '120px' },
  {
    key: 'phoneOverride', title: 'טלפון חלופי', sortable: false, minWidth: '120px',
    render: (v) => v || '—',
  },
  {
    key: 'status', title: 'סטטוס', sortable: true, minWidth: '90px',
    render: (v) => {
      const s = STATUS_LABEL[v] || { label: v, color: 'default' };
      return <Chip label={s.label} color={s.color} size="small" />;
    },
  },
  {
    key: 'source', title: 'מקור', sortable: true, minWidth: '70px',
    render: (v) => (v === 'web' ? '🌐 ווב' : '📞 ימות'),
  },
  {
    key: 'firstNameRecording', title: 'שם פרטי', sortable: false, minWidth: '100px',
    render: (v, row) => {
      if (row.source === 'web' && row.firstName) return row.firstName;
      return v ? <AudioPlayer path={v} label="שם פרטי" /> : '—';
    },
  },
  {
    key: 'lastNameRecording', title: 'שם משפחה', sortable: false, minWidth: '100px',
    render: (v, row) => {
      if (row.source === 'web' && row.lastName) return row.lastName;
      return v ? <AudioPlayer path={v} label="שם משפחה" /> : '—';
    },
  },
  {
    key: 'fatherNameRecording', title: 'שם אב', sortable: false, minWidth: '90px',
    render: (v, row) => {
      if (row.source === 'web' && row.fatherName) return row.fatherName;
      return v ? <AudioPlayer path={v} label="שם אב" /> : '—';
    },
  },
  {
    key: 'fatherInLawNameRecording', title: 'שם שווער', sortable: false, minWidth: '90px',
    render: (v, row) => {
      if (row.source === 'web' && row.fatherInLawName) return row.fatherInLawName;
      return v ? <AudioPlayer path={v} label="שם שווער" /> : '—';
    },
  },
  {
    key: 'addressRecording', title: 'כתובת', sortable: false, minWidth: '100px',
    render: (v, row) => {
      if (row.source === 'web' && row.address) return row.address;
      return v ? <AudioPlayer path={v} label="כתובת" /> : '—';
    },
  },
  ...(IS_MISHTAMERET ? [
    {
      key: 'updateMemberId', title: 'סוג', sortable: false, minWidth: '90px',
      render: (v) => (v ? <Chip label="עדכון פרטים" color="info" size="small" variant="outlined" /> : 'חדש'),
    },
    {
      key: 'city', title: 'עיר', sortable: true, minWidth: '100px',
      render: (v, row) => v || (row.cityRecording ? <AudioPlayer path={row.cityRecording} label="עיר" /> : '—'),
    },
    { key: 'idNumber', title: 'ת״ז / קוד', sortable: false, minWidth: '100px', render: (v) => v || '—' },
    { key: 'occupation', title: 'סטטוס', sortable: true, minWidth: '110px', render: (v) => v || '—' },
  ] : []),
  ...(!IS_MISHTAMERET ? [{
    key: 'kolelName', title: IS_OPEN_GEMARA ? 'כולל' : 'כוללים', sortable: false, minWidth: '160px',
    render: (v, row) => {
      if (IS_OPEN_GEMARA) {
        return row.kolelMorningName || row.kolelName || v || '—';
      }
      const parts = [
        row.kolelMorningName && `לפנה״צ: ${row.kolelMorningName}`,
        row.kolelAfternoonName && `אחה״צ: ${row.kolelAfternoonName}`,
        row.kolelEveningName && `ערב: ${row.kolelEveningName}`,
      ].filter(Boolean);
      if (parts.length) return parts.join(' | ');
      return v || '—';
    },
  }] : []),
  {
    key: 'examCode', title: 'קוד מבחן', sortable: false, minWidth: '90px',
    render: (v, row) => v || row.examId?.code || '—',
  },
  {
    key: 'examLocation', title: 'עיר מבחן', sortable: false, minWidth: '100px',
    render: (v) => v || '—',
  },
  {
    key: 'examId', title: 'מבחן', sortable: false, minWidth: '140px',
    render: (v) => (v && typeof v === 'object'
      ? `${v.name || ''} ${v.subject || ''}`.trim() || '—'
      : '—'),
  },
];

export default function PendingRegistrationsList() {
  const inNextDesign = useCkInside();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // דיאלוג אישור
  const [approveOpen,    setApproveOpen]    = useState(false);
  const [approveTarget,  setApproveTarget]  = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveForm, setApproveForm] = useState({
    firstName: '', lastName: '',
    fatherName: '', fatherInLawName: '', occupation: '',
    city: '', street: '', buildingNumber: '', apartment: '', entrance: '',
    kolelMorning: '', kolelMorningRef: null,
    kolelAfternoon: '', kolelAfternoonRef: null,
    kolelEvening: '', kolelEveningRef: null,
    notes: '',
  });
  const [approveError, setApproveError] = useState('');
  const [existingMember, setExistingMember] = useState(null);
  const [kolels, setKolels] = useState([]);
  const [notifyAutomationEnabled, setNotifyAutomationEnabled] = useState(false);
  const [sendNotification, setSendNotification] = useState(false);

  // דיאלוג דחייה
  const [rejectOpen,    setRejectOpen]    = useState(false);
  const [rejectTarget,  setRejectTarget]  = useState(null);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectNote,    setRejectNote]    = useState('');
  const [rejectError,   setRejectError]   = useState('');

  const classifySchedule = (schedule) => {
    const s = (schedule || '').trim();
    if (s.includes('בוקר') || s.includes('לפנה') || s.includes('לפני')) return 'morning';
    if (s.includes('אחה') || s.includes('צהר')) return 'afternoon';
    if (s.includes('ערב')) return 'evening';
    return 'unknown';
  };

  const kolelsForSlot = (slot, currentRef) => {
    const list = kolels.filter((k) => {
      const c = classifySchedule(k.schedule);
      return c === slot || c === 'unknown' || (k.schedule || '').includes('כל היום');
    });
    if (currentRef && !list.some((k) => k._id === currentRef)) {
      const current = kolels.find((k) => k._id === currentRef);
      if (current) return [current, ...list];
    }
    return list;
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/pending-registrations', { params: { page: 1, pageSize: 100000 } });
      setAllData(res.data.data);
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // טעינת רשימת כוללים לבחירה בדיאלוג אישור (לא רלוונטי למשתמרת)
  useEffect(() => {
    if (IS_MISHTAMERET) return;
    api.get('/kolels', { params: { pageSize: 500, status: 'active' } })
      .then(res => setKolels(res.data.data || []))
      .catch(() => {});
  }, []);

  // סטטוס אוטומציית הודעת אישור (לצ׳קבוקס בדיאלוג)
  useEffect(() => {
    api.get('/pending-registrations/notification-settings')
      .then((res) => setNotifyAutomationEnabled(Boolean(res.data?.enabled)))
      .catch(() => setNotifyAutomationEnabled(false));
  }, []);

  const resolvePendingRef = (row, slotRef, fallbackRef) => {
    const raw = row[slotRef] || row[fallbackRef] || null;
    if (!raw) return null;
    return typeof raw === 'object' ? (raw._id || null) : raw;
  };

  // ── פתיחת דיאלוג אישור ──────────────────────────────────────────────────
  const openApprove = (row) => {
    setApproveTarget(row);
    const isWeb = row.source === 'web';
    const morningRef = resolvePendingRef(row, 'kolelMorningRef', null)
      || (!row.kolelAfternoonRef && !row.kolelEveningRef ? resolvePendingRef(row, 'kolelRef', null) : null);
    const afternoonRef = resolvePendingRef(row, 'kolelAfternoonRef', null);
    const eveningRef = resolvePendingRef(row, 'kolelEveningRef', null);
    setApproveForm({
      firstName: isWeb ? (row.firstName || '') : '',
      lastName: isWeb ? (row.lastName || '') : '',
      fatherName: isWeb ? (row.fatherName || '') : '',
      fatherInLawName: isWeb ? (row.fatherInLawName || '') : '',
      occupation: row.occupation || '',
      city: row.city || '',
      street: isWeb ? (row.address || '') : '',
      buildingNumber: '',
      apartment: '',
      entrance: '',
      kolelMorning: row.kolelMorningName || (!row.kolelAfternoonName && !row.kolelEveningName ? (row.kolelName || '') : ''),
      kolelMorningRef: morningRef,
      kolelAfternoon: row.kolelAfternoonName || '',
      kolelAfternoonRef: afternoonRef,
      kolelEvening: row.kolelEveningName || '',
      kolelEveningRef: eveningRef,
      notes: '',
    });
    setApproveError('');
    setExistingMember(null);
    setSendNotification(notifyAutomationEnabled);
    setApproveOpen(true);
  };

  const buildApprovePayload = (updateExisting = false) => {
    let base = approveForm;
    if (IS_MISHTAMERET) {
      base = {
        ...approveForm,
        kolelMorning: '',
        kolelMorningRef: null,
        kolelAfternoon: '',
        kolelAfternoonRef: null,
        kolelEvening: '',
        kolelEveningRef: null,
      };
    } else if (IS_OPEN_GEMARA) {
      base = {
        ...approveForm,
        kolelAfternoon: '',
        kolelAfternoonRef: null,
        kolelEvening: '',
        kolelEveningRef: null,
      };
    }
    const withNotify = { ...base, sendNotification: Boolean(sendNotification) };
    return updateExisting ? { ...withNotify, updateExisting: true } : withNotify;
  };

  const handleApprove = async (updateExisting = false) => {
    if (!approveForm.firstName.trim() || !approveForm.lastName.trim()) {
      setApproveError('יש למלא שם פרטי ושם משפחה');
      return;
    }
    setApproveLoading(true);
    setApproveError('');
    if (!updateExisting) setExistingMember(null);
    try {
      await api.post(
        `/pending-registrations/${approveTarget._id}/approve`,
        buildApprovePayload(updateExisting),
      );
      setApproveOpen(false);
      setExistingMember(null);
      loadData();
    } catch (e) {
      const data = e.response?.data;
      if (e.response?.status === 409 && data?.code === 'PHONE_EXISTS') {
        setExistingMember(data.existingMember || null);
        setApproveError(data.error || 'החבר כבר רשום במערכת עם מספר טלפון זה');
      } else {
        setExistingMember(null);
        setApproveError(data?.error || 'שגיאה באישור');
      }
    } finally {
      setApproveLoading(false);
    }
  };

  // ── פתיחת דיאלוג דחייה ──────────────────────────────────────────────────
  const openReject = (row) => {
    setRejectTarget(row);
    setRejectNote('');
    setRejectError('');
    setRejectOpen(true);
  };

  const handleReject = async () => {
    setRejectLoading(true);
    setRejectError('');
    try {
      await api.post(`/pending-registrations/${rejectTarget._id}/reject`, { notes: rejectNote });
      setRejectOpen(false);
      loadData();
    } catch (e) {
      setRejectError(e.response?.data?.error || 'שגיאה בדחייה');
    } finally {
      setRejectLoading(false);
    }
  };

  const columnsWithActions = [
    ...COLUMNS,
    {
      key: '_actions', title: 'פעולות', sortable: false, filterable: false, minWidth: '110px',
      render: (_, row) => row.status === 'pending' ? (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="אשר ורשום חבר">
            <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); openApprove(row); }}>
              <CheckCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="דחה">
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openReject(row); }}>
              <CancelOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : null,
    },
  ];

  return (
    <Box sx={{ p: inNextDesign ? 0 : 3 }}>
      <AdminPageHeader
        title="בקשות רישום ממתינות"
        sub={!loading ? `${allData.filter((r) => r.status === 'pending').length.toLocaleString('he-IL')} ממתינות לטיפול · ${allData.length.toLocaleString('he-IL')} בסך הכול` : undefined}
        legacy={(
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <HowToRegOutlinedIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>בקשות רישום ממתינות</Typography>
          </Stack>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableKit
        columns={columnsWithActions}
        data={allData}
        total={allData.length}
        loading={loading}
        clientSideMode
        exportFileName="בקשות רישום"
      />

      {/* ── דיאלוג אישור ─────────────────────────────────────────────── */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="sm" fullWidth dir="rtl" disableEnforceFocus>
        <DialogTitle>אישור בקשת רישום</DialogTitle>
        <DialogContent dividers>
          {approveTarget && (
            <Stack spacing={2} mt={1}>
              <Typography variant="body2" color="text.secondary">
                טלפון מתקשר: <strong>{approveTarget.phone}</strong>
                {approveTarget.phoneOverride && <> | טלפון חלופי: <strong>{approveTarget.phoneOverride}</strong></>}
                {approveTarget.source === 'web' && <> | מקור: <strong>ווב</strong></>}
                {IS_MISHTAMERET && approveTarget.idNumber && <> | ת״ז / קוד: <strong>{approveTarget.idNumber}</strong></>}
                {IS_MISHTAMERET && approveTarget.occupation && <> | סטטוס: <strong>{approveTarget.occupation}</strong></>}
                {IS_MISHTAMERET && approveTarget.email && <> | מייל: <strong>{approveTarget.email}</strong></>}
              </Typography>

              {approveTarget.updateMemberId && (
                <Alert severity="warning">
                  בקשת עדכון פרטים לחבר קיים:{' '}
                  <strong>
                    {[approveTarget.updateMemberId.firstName, approveTarget.updateMemberId.lastName].filter(Boolean).join(' ') || '—'}
                  </strong>
                  {approveTarget.updateMemberId.phone && <> | טלפון נוכחי: <strong>{approveTarget.updateMemberId.phone}</strong></>}
                  {approveTarget.updateMemberId.city && <> | עיר נוכחית: <strong>{approveTarget.updateMemberId.city}</strong></>}
                  <br />
                  האישור יעדכן את החבר הקיים (שדות ריקים לא ידרסו).
                </Alert>
              )}

              {/* האזנה להקלטות / הצגת טקסט מווב */}
              {approveTarget.source === 'web' ? (
                <Alert severity="info">
                  בקשה מהאתר — הפרטים מולאו מראש.
                  {notifyAutomationEnabled
                    ? ' ניתן לשלוח הודעה קולית לנרשם לפי הגדרות תבניות IVR.'
                    : ' אוטומציית הודעת אישור כבויה (ניתן להפעיל במסך תבניות IVR).'}
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  האזן להקלטות ומלא את הפרטים:
                </Typography>
              )}
              <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
                {approveTarget.firstNameRecording && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2">שם פרטי:</Typography>
                    <AudioPlayer path={approveTarget.firstNameRecording} label="שם פרטי" />
                  </Stack>
                )}
                {approveTarget.lastNameRecording && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2">שם משפחה:</Typography>
                    <AudioPlayer path={approveTarget.lastNameRecording} label="שם משפחה" />
                  </Stack>
                )}
                {approveTarget.fatherNameRecording && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2">שם אב:</Typography>
                    <AudioPlayer path={approveTarget.fatherNameRecording} label="שם אב" />
                  </Stack>
                )}
                {approveTarget.fatherInLawNameRecording && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2">שם שווער:</Typography>
                    <AudioPlayer path={approveTarget.fatherInLawNameRecording} label="שם שווער" />
                  </Stack>
                )}
                {approveTarget.addressRecording && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2">כתובת:</Typography>
                    <AudioPlayer path={approveTarget.addressRecording} label="כתובת" />
                  </Stack>
                )}
                {approveTarget.cityRecording && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2">עיר:</Typography>
                    <AudioPlayer path={approveTarget.cityRecording} label="עיר" />
                  </Stack>
                )}
              </Stack>

              {/* שם */}
              <Stack direction="row" spacing={2}>
                <TextField
                  label="שם פרטי *" size="small" fullWidth
                  value={approveForm.firstName}
                  onChange={(e) => setApproveForm(f => ({ ...f, firstName: e.target.value }))}
                />
                <TextField
                  label="שם משפחה *" size="small" fullWidth
                  value={approveForm.lastName}
                  onChange={(e) => setApproveForm(f => ({ ...f, lastName: e.target.value }))}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="שם האב" size="small" fullWidth
                  value={approveForm.fatherName}
                  onChange={(e) => setApproveForm(f => ({ ...f, fatherName: e.target.value }))}
                />
                <TextField
                  label="שם השווער" size="small" fullWidth
                  value={approveForm.fatherInLawName}
                  onChange={(e) => setApproveForm(f => ({ ...f, fatherInLawName: e.target.value }))}
                />
              </Stack>
              {IS_MISHTAMERET && (
                <TextField
                  select label="סטטוס" size="small" fullWidth
                  value={approveForm.occupation}
                  onChange={(e) => setApproveForm(f => ({ ...f, occupation: e.target.value }))}
                >
                  <MenuItem value="">לא צוין</MenuItem>
                  {[...OCCUPATION_OPTIONS, ...(approveForm.occupation && !OCCUPATION_OPTIONS.includes(approveForm.occupation) ? [approveForm.occupation] : [])].map((o) => (
                    <MenuItem key={o} value={o}>{o}</MenuItem>
                  ))}
                </TextField>
              )}

              {/* כתובת */}
              <Typography variant="caption" color="text.secondary" fontWeight={600}>כתובת:</Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="עיר" size="small" fullWidth
                  value={approveForm.city}
                  onChange={(e) => setApproveForm(f => ({ ...f, city: e.target.value }))}
                />
                <TextField
                  label="רחוב" size="small" fullWidth
                  value={approveForm.street}
                  onChange={(e) => setApproveForm(f => ({ ...f, street: e.target.value }))}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="מספר בניין" size="small" fullWidth
                  value={approveForm.buildingNumber}
                  onChange={(e) => setApproveForm(f => ({ ...f, buildingNumber: e.target.value }))}
                />
                <TextField
                  label="כניסה" size="small" fullWidth
                  value={approveForm.entrance}
                  onChange={(e) => setApproveForm(f => ({ ...f, entrance: e.target.value }))}
                />
                <TextField
                  label="דירה" size="small" fullWidth
                  value={approveForm.apartment}
                  onChange={(e) => setApproveForm(f => ({ ...f, apartment: e.target.value }))}
                />
              </Stack>

              {!IS_MISHTAMERET && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {IS_OPEN_GEMARA ? 'כולל:' : 'כוללים:'}
                  </Typography>
                  {IS_OPEN_GEMARA ? (
                    <Autocomplete
                      size="small" fullWidth
                      options={kolels}
                      getOptionLabel={(o) => `${o.name || ''}${o.city ? ` (${o.city})` : ''}${o.code != null ? ` — ${o.code}` : ''}`}
                      isOptionEqualToValue={(opt, val) => opt._id === val?._id}
                      value={kolels.find(k => k._id === approveForm.kolelMorningRef) || null}
                      onChange={(_, v) => setApproveForm(f => ({
                        ...f,
                        kolelMorningRef: v?._id || null,
                        kolelMorning: v?.name || '',
                        kolelAfternoonRef: null,
                        kolelAfternoon: '',
                        kolelEveningRef: null,
                        kolelEvening: '',
                      }))}
                      renderInput={(params) => <TextField {...params} label="כולל" />}
                    />
                  ) : (
                    <>
                      <Autocomplete
                        size="small" fullWidth
                        options={kolelsForSlot('morning', approveForm.kolelMorningRef)}
                        getOptionLabel={(o) => `${o.name || ''}${o.city ? ` (${o.city})` : ''}${o.code != null ? ` — ${o.code}` : ''}`}
                        isOptionEqualToValue={(opt, val) => opt._id === val?._id}
                        value={kolels.find(k => k._id === approveForm.kolelMorningRef) || null}
                        onChange={(_, v) => setApproveForm(f => ({
                          ...f,
                          kolelMorningRef: v?._id || null,
                          kolelMorning: v?.name || '',
                        }))}
                        renderInput={(params) => <TextField {...params} label='כולל לפנה"צ' />}
                      />
                      <Autocomplete
                        size="small" fullWidth
                        options={kolelsForSlot('afternoon', approveForm.kolelAfternoonRef)}
                        getOptionLabel={(o) => `${o.name || ''}${o.city ? ` (${o.city})` : ''}${o.code != null ? ` — ${o.code}` : ''}`}
                        isOptionEqualToValue={(opt, val) => opt._id === val?._id}
                        value={kolels.find(k => k._id === approveForm.kolelAfternoonRef) || null}
                        onChange={(_, v) => setApproveForm(f => ({
                          ...f,
                          kolelAfternoonRef: v?._id || null,
                          kolelAfternoon: v?.name || '',
                        }))}
                        renderInput={(params) => <TextField {...params} label='כולל אחה"צ' />}
                      />
                      <Autocomplete
                        size="small" fullWidth
                        options={kolelsForSlot('evening', approveForm.kolelEveningRef)}
                        getOptionLabel={(o) => `${o.name || ''}${o.city ? ` (${o.city})` : ''}${o.code != null ? ` — ${o.code}` : ''}`}
                        isOptionEqualToValue={(opt, val) => opt._id === val?._id}
                        value={kolels.find(k => k._id === approveForm.kolelEveningRef) || null}
                        onChange={(_, v) => setApproveForm(f => ({
                          ...f,
                          kolelEveningRef: v?._id || null,
                          kolelEvening: v?.name || '',
                        }))}
                        renderInput={(params) => <TextField {...params} label="כולל ערב" />}
                      />
                    </>
                  )}

                  {(approveTarget.kolelMorningName || approveTarget.kolelAfternoonName || approveTarget.kolelEveningName || approveTarget.kolelName) && (
                    <Typography variant="caption" color="info.main">
                      הנרשם בחר:{' '}
                      <strong>
                        {IS_OPEN_GEMARA
                          ? (approveTarget.kolelMorningName || approveTarget.kolelName)
                          : ([
                              approveTarget.kolelMorningName && `לפנה״צ: ${approveTarget.kolelMorningName}`,
                              approveTarget.kolelAfternoonName && `אחה״צ: ${approveTarget.kolelAfternoonName}`,
                              approveTarget.kolelEveningName && `ערב: ${approveTarget.kolelEveningName}`,
                            ].filter(Boolean).join(' | ')
                              || approveTarget.kolelName)}
                      </strong>
                    </Typography>
                  )}
                </>
              )}

              <TextField
                label="הערות" size="small" fullWidth multiline rows={2}
                value={approveForm.notes}
                onChange={(e) => setApproveForm(f => ({ ...f, notes: e.target.value }))}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendNotification}
                    disabled={!notifyAutomationEnabled}
                    onChange={(e) => setSendNotification(e.target.checked)}
                  />
                }
                label={
                  notifyAutomationEnabled
                    ? 'שלח הודעה קולית לנרשם'
                    : 'שלח הודעה קולית לנרשם (האוטומציה כבויה)'
                }
              />

              {approveError && (
                <Alert severity="error">
                  {approveError}
                  {existingMember && (
                    <Box mt={1}>
                      חבר קיים:{' '}
                      <strong>
                        {[existingMember.firstName, existingMember.lastName].filter(Boolean).join(' ') || '—'}
                      </strong>
                      {existingMember.personalCode != null && <> | קוד אישי: <strong>{existingMember.personalCode}</strong></>}
                      {existingMember.phone && <> | טלפון: <strong>{existingMember.phone}</strong></>}
                      {existingMember.city && <> | עיר: <strong>{existingMember.city}</strong></>}
                    </Box>
                  )}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} disabled={approveLoading}>ביטול</Button>
          {existingMember && (
            <Button
              variant="contained"
              color="warning"
              onClick={() => handleApprove(true)}
              disabled={approveLoading}
              startIcon={approveLoading ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
            >
              עדכן חבר קיים
            </Button>
          )}
          <Button
            variant="contained" color="success" onClick={() => handleApprove(false)}
            disabled={approveLoading}
            startIcon={approveLoading ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
          >
            {approveTarget?.updateMemberId ? 'אשר ועדכן חבר' : 'אשר וצור חבר'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── דיאלוג דחייה ─────────────────────────────────────────────── */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth dir="rtl">
        <DialogTitle>דחיית בקשת רישום</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2">
              טלפון: <strong>{rejectTarget?.phone}</strong>
            </Typography>
            <TextField
              label="סיבת דחייה (אופציונלי)" size="small" fullWidth multiline rows={2}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
            {rejectError && <Alert severity="error">{rejectError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={rejectLoading}>ביטול</Button>
          <Button
            variant="contained" color="error" onClick={handleReject}
            disabled={rejectLoading}
            startIcon={rejectLoading ? <CircularProgress size={16} /> : <CancelOutlinedIcon />}
          >
            דחה
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
